import { useEffect, useState } from "react";
import { EditPanDetailsModal } from "../EditPanDetailsModal/EditPanDetailsModal";
import { EditBankDetailsModal } from "../EditBankDetailsModal/EditBankDetailsModal";
import { EditPanAndBankDetailsModal } from "../EditPanAndBankDetailsModal/EditPanAndBankDetailsModal";
import { useFetchKycStatus, useFetchPanDetails, useFetchBankDetails } from "../../services/api";
import { mapKycStatus, mapPanDetails, mapBankDetails } from "../../services/mappers/kycMapper";
import { KYC_STATUS_UPDATED_EVENT } from "../AddPanAndBankDetails/AddPanAndBankDetails";
import { getUserId } from "../../../../utils/auth";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { DESKTOP_BREAKPOINT } from "../../consts";
import editIcon from "../../../../assets/images/pencil_line.png";
import styles from "./style.module.css";

/**
 *
 *
 * @param {Object} props
 * @param {() => void} props.onEditProfile - Opens the existing profile-edit flow.
 * @param {string} [props.className] - Extra class merged onto both buttons (visual: padding,
 *   font-size, gap, justify-content). Sizing/flex properties won't reach the buttons now that
 *   they're nested in a wrapper div — pass those via wrapperClassName instead.
 * @param {string} [props.wrapperClassName] - Extra class merged onto the outer wrapper div —
 *   this is the actual flex child when EditDetailsButton sits in a flex row (e.g. nameActions'
 *  
 */
export const EditDetailsButton = ({
  onEditProfile,
  className = "",
  wrapperClassName = "",
}) => {
  const userId = getUserId();

  const isMobile = useMediaQuery(`(max-width:${DESKTOP_BREAKPOINT - 0.05}px)`);

  const [isPanModalOpen, setIsPanModalOpen] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isPanAndBankModalOpen, setIsPanAndBankModalOpen] = useState(false);

  const [kycStatus, setKycStatus] = useState({ hasPan: false, hasBank: false });

  const { loading: kycStatusLoading, refetch: refetchKycStatus } = useFetchKycStatus(userId, {
    onSuccess: (data) => setKycStatus(mapKycStatus(data)),
    onError: () => setKycStatus({ hasPan: false, hasBank: false }),
  });


  useEffect(() => {
    window.addEventListener(KYC_STATUS_UPDATED_EVENT, refetchKycStatus);
    return () => window.removeEventListener(KYC_STATUS_UPDATED_EVENT, refetchKycStatus);
  }, [refetchKycStatus]);

  const { apiData: rawPanDetails, refetch: refetchPanDetails } = useFetchPanDetails(userId, {
    manual: true,
  });
  const { apiData: rawBankDetails, refetch: refetchBankDetails } = useFetchBankDetails(userId, {
    manual: true,
  });

  const panDetails = kycStatus.hasPan && rawPanDetails ? mapPanDetails(rawPanDetails) : {};
  const bankDetails = kycStatus.hasBank && rawBankDetails ? mapBankDetails(rawBankDetails) : {};

  const { hasPan, hasBank } = kycStatus;

  const handleEditPanOrBank = async () => {
    const fetches = [];
    if (hasPan) fetches.push(refetchPanDetails());
    if (hasBank) fetches.push(refetchBankDetails());
    if (fetches.length) await Promise.all(fetches);

    if (hasPan && !hasBank) {
      setIsPanModalOpen(true);
    } else if (hasBank && !hasPan) {
      setIsBankModalOpen(true);
    } else {
      setIsPanAndBankModalOpen(true);
    }
  };

  const refreshAfterSave = () => {
    refetchKycStatus();
    if (hasPan) refetchPanDetails();
    if (hasBank) refetchBankDetails();
  };

  return (
    <div className={`${styles.wrapper} ${wrapperClassName}`}>
      <button
        type="button"
        className={`${styles.trigger} ${className}`}
        onClick={onEditProfile}
      >
        {isMobile ? "Edit profile" : "Edit profile details"}
        <img src={editIcon} alt="edit" className={styles.triggerIcon} />
      </button>

      {(hasPan || hasBank) && (
        <button
          type="button"
          className={`${styles.trigger} ${className}`}
          onClick={handleEditPanOrBank}
          disabled={kycStatusLoading}
        >
          {isMobile ? "PAN/Bank" : "Edit PAN / Bank details"}
          <img src={editIcon} alt="edit" className={styles.triggerIcon} />
        </button>
      )}

      <EditPanDetailsModal
        isOpen={isPanModalOpen}
        onClose={() => setIsPanModalOpen(false)}
        currentDetails={panDetails}
        onSaveSuccess={refreshAfterSave}
      />

      <EditBankDetailsModal
        isOpen={isBankModalOpen}
        onClose={() => setIsBankModalOpen(false)}
        currentDetails={bankDetails}
        onSaveSuccess={refreshAfterSave}
      />

      <EditPanAndBankDetailsModal
        isOpen={isPanAndBankModalOpen}
        onClose={() => setIsPanAndBankModalOpen(false)}
        panDetails={panDetails}
        bankDetails={bankDetails}
        onSaveSuccess={refreshAfterSave}
      />
    </div>
  );
};
